import { useTranslation } from 'react-i18next';
import { ExternalLinkIcon, XIcon } from 'lucide-react';
import { Button } from '@noor/ui';
import { dayjs } from '@noor/lib';
import {
  OFFER_WRONG_EQUIPMENT_REASON_ID,
  decodeDecisionStage,
  type PlottedOffer,
} from '@/entities/order';
import { useCan } from '@/entities/access';
import { toneFill, toneInk } from '../lib/offer-colors';
import { outcomeLabel, presentText, shortCourierId } from '../lib/offer-labels';
import type { OfferMarker } from '../lib/offer-markers';

interface OfferDetailsCardProps {
  marker: OfferMarker;
  onClose: () => void;
}

interface DetailRow {
  label: string;
  value: string;
}

/** `HH:mm:ss`, or null when the stamp is missing/unparseable. */
function clockSeconds(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('HH:mm:ss') : null;
}

/**
 * What happened with one courier's offer — cpanel's marker popover, as a card
 * pinned to the map corner (a popover would sit on top of the very markers the
 * operator is comparing, and cannot be reached by keyboard inside the map).
 */
export function OfferDetailsCard({ marker, onClose }: OfferDetailsCardProps) {
  const { t } = useTranslation('orders');
  const { offer, outcome } = marker;
  const rows = useDetailRows(marker);

  return (
    <section
      aria-label={t('offers.details.title')}
      className="absolute inset-x-3 bottom-3 z-[1000] animate-in overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-lg fade-in-0 slide-in-from-bottom-2 sm:inset-x-auto sm:top-3 sm:right-3 sm:bottom-auto sm:w-72"
    >
      <header
        className="flex items-center justify-between gap-2 px-3 py-2"
        style={{ backgroundColor: toneFill(outcome.tone), color: toneInk(outcome.tone) }}
      >
        <h3 className="min-w-0 text-sm leading-snug font-medium">
          {outcomeLabel(t, outcome)}
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('offers.details.close')}
          className="-mr-1 grid size-6 shrink-0 cursor-pointer place-items-center rounded-md outline-none hover:bg-foreground/10 focus-visible:ring-2 focus-visible:ring-current"
        >
          <XIcon className="size-4" />
        </button>
      </header>

      <div className="space-y-3 p-3">
        <dl className="grid grid-cols-3 gap-x-3 gap-y-2.5 sm:grid-cols-2">
          {rows.map((row) => (
            <div key={row.label} className="min-w-0">
              <dt className="text-[11px] text-muted-foreground">{row.label}</dt>
              <dd className="truncate text-sm font-medium tabular-nums">{row.value}</dd>
            </div>
          ))}
        </dl>
        {offer.mode === 'legacy' ? (
          <LegacyCourierName offer={offer} />
        ) : (
          <NewCourierIdentity offer={offer} />
        )}
      </div>
    </section>
  );
}

/**
 * The label/value rows of the card. The two sources record different things:
 * the Delivery service has creation/decision times and the dispatch stage;
 * cpanel's API has when the request reached the screen, when it was accepted,
 * and the vehicle of a wrong-equipment bounce.
 */
function useDetailRows({ offer, outcome, straightKm }: OfferMarker): DetailRow[] {
  const { t } = useTranslation('orders');
  const km = (value: number) => t('offers.km', { value: value.toFixed(1) });
  const distance = { label: t('offers.details.distance'), value: km(straightKm) };
  const route = {
    label: t('offers.details.route'),
    value: offer.roadMeters != null ? km(offer.roadMeters / 1000) : '—',
  };

  if (offer.mode === 'legacy') {
    const { raw } = offer;
    const vehicle =
      outcome.reason?.id === OFFER_WRONG_EQUIPMENT_REASON_ID && raw.equipment_name
        ? [{ label: t('offers.details.vehicle'), value: raw.equipment_name }]
        : [];
    return [
      {
        label: t('offers.details.id'),
        value: `#${presentText(offer.courierId) ?? t('offers.details.courierFallback')}`,
      },
      distance,
      route,
      {
        label: t('offers.details.shownAt'),
        value: clockSeconds(raw.offered_at) ?? t('offers.details.notShown'),
      },
      {
        label: t('offers.details.acceptedAt'),
        value: clockSeconds(raw.accepted_at) ?? t('offers.details.notAccepted'),
      },
      ...vehicle,
    ];
  }

  const stage = decodeDecisionStage(offer.raw.decision_detail);
  return [
    {
      label: t('offers.details.id'),
      value: `#${shortCourierId(offer.courierId) ?? t('offers.details.courierFallback')}`,
    },
    distance,
    route,
    {
      label: t('offers.details.created'),
      value: clockSeconds(offer.raw.created_at) ?? '—',
    },
    {
      label: t('offers.details.decided'),
      value: clockSeconds(offer.raw.updated_at) ?? '—',
    },
    ...(stage ? [{ label: t('offers.details.stage'), value: stage }] : []),
  ];
}

/** cpanel's "Имя, Фамилия" line, with its `Courier` / `#id` fallbacks. */
function LegacyCourierName({
  offer,
}: {
  offer: Extract<PlottedOffer, { mode: 'legacy' }>;
}) {
  const { t } = useTranslation('orders');
  const { raw } = offer;
  const first =
    presentText(raw.courier_first_name) ?? t('offers.details.courierFallback');
  const last =
    presentText(raw.courier_last_name) ?? `#${presentText(offer.courierId) ?? '-'}`;
  return (
    <>
      <div className="min-w-0">
        <p className="text-[11px] text-muted-foreground">{t('offers.details.name')}</p>
        <p className="truncate text-sm font-medium">
          {first} {last}
        </p>
      </div>
      <CourierProfileLink courierId={offer.courierId} />
    </>
  );
}

/** The Delivery service names couriers by UUID only: show it whole, and link out. */
function NewCourierIdentity({
  offer,
}: {
  offer: Extract<PlottedOffer, { mode: 'new' }>;
}) {
  const { t } = useTranslation('orders');
  const courierId = offer.courierId;
  if (!shortCourierId(courierId)) return null;
  return (
    <>
      <div className="min-w-0">
        <p className="text-[11px] text-muted-foreground">
          {t('offers.details.courierId')}
        </p>
        <p className="font-mono text-xs break-all select-all">{String(courierId)}</p>
      </div>
      <CourierProfileLink courierId={courierId} />
    </>
  );
}

/** Opens the courier's profile in a new tab (for operators allowed to see couriers). */
function CourierProfileLink({ courierId }: { courierId: string | number | null }) {
  const { t } = useTranslation('orders');
  const canOpenCourier = useCan('couriers.view');
  const id = presentText(courierId);
  if (id === null || !canOpenCourier) return null;
  return (
    <Button variant="soft" size="sm" shape="rounded" className="w-full" asChild>
      {/* Modals mount above the router, so a router <Link> cannot render
          here — a plain link in a new tab also keeps the operator's place. */}
      <a
        href={`/couriers/${encodeURIComponent(id)}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {t('offers.details.openProfile')}
        <ExternalLinkIcon />
      </a>
    </Button>
  );
}
